"use client";

import React, { ReactNode, useEffect, useRef } from "react";
import { attachDragScroll } from "@/components/DragScroll/DragScroll";

// Native CSS scroll-snap slider with the vanilla controls the live Swiper sliders had:
// a draggable progress scrollbar, prev/next arrows, and pagination bullets — WITHOUT shipping
// Swiper (~33KB). It deliberately renders the legacy Swiper class names (.swiper, .swiper-wrapper,
// .swiper-slide, .swiper-scrollbar*, .swiper-button-prev/next, .swiper-pagination*) so every
// existing skin rule in global.scss and the module SCSS (sliderScrollButtonsBottom mixin, module
// overrides) applies unchanged and the sliders look pixel-identical to production. Swiper's own
// base CSS is gone from the bundle; the few structural rules it used to provide (button
// positioning, 98% scrollbar width, bullet shape) now live in global.scss under `.swiper`.
//
// Slides stay server-rendered (children of a client component keep their RSC payload), only the
// control handlers hydrate.

interface SnapSliderProps {
  children: ReactNode;
  /** Extra classes for the .swiper root (module hooks). */
  className?: string;
  /** Classes on the scrolling track (gaps, padding, full-bleed). */
  trackClassName?: string;
  /** Classes on each slide cell (width control). Only used when wrapItems is true. */
  itemClassName?: string;
  /** Wrap each child in a .swiper-slide cell (default). Pass false when children already are the
   * sized/snapped slide elements (e.g. the case-study items). */
  wrapItems?: boolean;
  /** "mandatory" snaps hard to each slide (Swiper's default mode); "none" = free scrolling
   * (Swiper's FreeMode); "proximity" is a softer snap. */
  snap?: "mandatory" | "proximity" | "none";
  /** Break the track out of the centered container to the viewport edges (live Swiper had
   * overflow:visible, so slides bled to the screen edge). Uses the .full-bleed-track utility whose
   * padding AND scroll-padding both derive from the container CSS vars, so snap positions stay
   * aligned with the container's left edge. */
  fullBleed?: boolean;
  /** Draggable 2px progress scrollbar under the slides (Swiper Scrollbar). */
  scrollbar?: boolean;
  /** Prev/next arrow buttons (Swiper Navigation). Positioning comes from the consumer's SCSS. */
  navigation?: boolean;
  /** Bullet pagination (Swiper Pagination). One bullet per slide. */
  pagination?: boolean;
}

export default function SnapSlider({
  children,
  className = "",
  trackClassName = "gap-6",
  itemClassName = "",
  wrapItems = true,
  snap = "mandatory",
  fullBleed = false,
  scrollbar = false,
  navigation = false,
  pagination = false,
}: SnapSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const pagRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const bar = barRef.current;
    const drag = dragRef.current;
    const prev = prevRef.current;
    const next = nextRef.current;
    const pag = pagRef.current;
    const cleanups: Array<() => void> = [attachDragScroll(track)];

    const maxScroll = () => track.scrollWidth - track.clientWidth;

    // Snap positions: scrollLeft values that align each slide's left edge with the track's
    // scroll-padding-left (the container's left edge for full-bleed tracks).
    const positions = (): number[] => {
      const tr = track.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      return Array.from(track.children)
        .map((el) => {
          const r = el.getBoundingClientRect();
          return { x: r.left - tr.left + track.scrollLeft - pad, w: r.width };
        })
        .filter((p) => p.w > 0) // skip breakpoint-hidden slides
        .map((p) => Math.max(0, Math.min(p.x, maxScroll())));
    };

    const goTo = (x: number) =>
      track.scrollTo({ left: Math.max(0, Math.min(x, maxScroll())), behavior: "smooth" });

    // Sync controls to the scroll position. rAF-throttled: runs at most once a frame.
    let raf = 0;
    const update = () => {
      raf = 0;
      // Mirror the track's own padding into scroll-padding: percentage padding (the full-bleed
      // container inset) resolves against the parent, but scroll-padding percentages resolve
      // against the scrollport — so snap positions would align slides to the viewport edge
      // instead of the parent's edge without this.
      const cs = getComputedStyle(track);
      if (track.style.scrollPaddingLeft !== cs.paddingLeft) {
        track.style.scrollPaddingLeft = cs.paddingLeft;
        track.style.scrollPaddingRight = cs.paddingRight;
      }
      const ms = maxScroll();
      const sl = track.scrollLeft;
      if (prev) prev.classList.toggle("swiper-button-disabled", sl <= 1);
      if (next) next.classList.toggle("swiper-button-disabled", sl >= ms - 1);
      if (bar && drag) {
        // An empty slider (e.g. the similar-products slider when the category filter matches
        // nothing) must not show a bare scrollbar track — live rendered nothing there.
        const hasSlides = Array.from(track.children).some(
          (c) => c.getBoundingClientRect().width > 0
        );
        bar.style.display = hasSlides ? "" : "none";
        const barW = bar.clientWidth;
        // Handle length = visible/total CONTENT ratio. Exclude the track's own padding (the
        // full-bleed container inset) from both sides, like Swiper's virtual size did — otherwise
        // the handle reads longer than the live one.
        const tcs = getComputedStyle(track);
        const padX = (parseFloat(tcs.paddingLeft) || 0) + (parseFloat(tcs.paddingRight) || 0);
        const total = track.scrollWidth - padX;
        const dragW = total > 0 ? Math.min(((track.clientWidth - padX) / total) * barW, barW) : barW;
        const x = ms > 0 ? (sl / ms) * (barW - dragW) : 0;
        drag.style.width = `${dragW}px`;
        drag.style.transform = `translate3d(${x}px, 0, 0)`;
      }
      if (pag) {
        const pos = positions();
        let active = 0;
        let bestD = Infinity;
        pos.forEach((x, i) => {
          const d = Math.abs(x - sl);
          if (d < bestD) { bestD = d; active = i; }
        });
        Array.from(pag.children).forEach((b, i) =>
          b.classList.toggle("swiper-pagination-bullet-active", i === active)
        );
      }
    };
    const requestUpdate = () => { if (!raf) raf = requestAnimationFrame(update); };

    track.addEventListener("scroll", requestUpdate, { passive: true });
    cleanups.push(() => track.removeEventListener("scroll", requestUpdate));

    const ro = new ResizeObserver(requestUpdate);
    ro.observe(track);
    Array.from(track.children).forEach((c) => ro.observe(c));
    const mo = new MutationObserver(() => {
      ro.disconnect();
      ro.observe(track);
      Array.from(track.children).forEach((c) => ro.observe(c));
      requestUpdate();
    });
    mo.observe(track, { childList: true });
    cleanups.push(() => { ro.disconnect(); mo.disconnect(); });

    // Prev/next: move to the nearest snap position behind/ahead of the current scroll offset
    // (one slide per click from a snapped state — Swiper's slidePrev/slideNext).
    if (prev && next) {
      const onPrev = () => {
        const behind = positions().filter((x) => x < track.scrollLeft - 1);
        goTo(behind.length ? behind[behind.length - 1] : 0);
      };
      const onNext = () => {
        const ahead = positions().find((x) => x > track.scrollLeft + 1);
        goTo(ahead !== undefined ? ahead : maxScroll());
      };
      const key = (fn: () => void) => (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); }
      };
      const onPrevKey = key(onPrev);
      const onNextKey = key(onNext);
      prev.addEventListener("click", onPrev);
      next.addEventListener("click", onNext);
      prev.addEventListener("keydown", onPrevKey);
      next.addEventListener("keydown", onNextKey);
      cleanups.push(() => {
        prev.removeEventListener("click", onPrev);
        next.removeEventListener("click", onNext);
        prev.removeEventListener("keydown", onPrevKey);
        next.removeEventListener("keydown", onNextKey);
      });
    }

    // Draggable scrollbar (Swiper's scrollbar: { draggable: true }). Pointer-drag maps bar travel
    // to scroll range; a bare click on the track jumps so the handle centers under the pointer.
    if (bar && drag) {
      let dragging = false;
      let startX = 0;
      let startLeft = 0;
      const toScroll = (dx: number) => {
        const usable = bar.clientWidth - drag.getBoundingClientRect().width;
        return usable > 0 ? (dx / usable) * maxScroll() : 0;
      };
      const down = (e: PointerEvent) => {
        dragging = true;
        startX = e.clientX;
        try { bar.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
        track.style.scrollSnapType = "none";
        track.style.scrollBehavior = "auto";
        e.preventDefault();
        if (e.target === bar) {
          const dragW = drag.getBoundingClientRect().width;
          track.scrollLeft = toScroll(e.clientX - bar.getBoundingClientRect().left - dragW / 2);
        }
        startLeft = track.scrollLeft;
      };
      const move = (e: PointerEvent) => {
        if (!dragging) return;
        track.scrollLeft = startLeft + toScroll(e.clientX - startX);
      };
      const up = (e: PointerEvent) => {
        if (!dragging) return;
        dragging = false;
        try { bar.releasePointerCapture(e.pointerId); } catch { /* nothing to release */ }
        track.style.scrollSnapType = "";
        track.style.scrollBehavior = "";
      };
      bar.addEventListener("pointerdown", down);
      bar.addEventListener("pointermove", move);
      bar.addEventListener("pointerup", up);
      bar.addEventListener("pointercancel", up);
      cleanups.push(() => {
        bar.removeEventListener("pointerdown", down);
        bar.removeEventListener("pointermove", move);
        bar.removeEventListener("pointerup", up);
        bar.removeEventListener("pointercancel", up);
      });
    }

    // Bullet click -> scroll to that slide.
    if (pag) {
      const onPagClick = (e: MouseEvent) => {
        const bullet = (e.target as HTMLElement).closest(".swiper-pagination-bullet");
        if (!bullet || !pag.contains(bullet)) return;
        const i = Array.from(pag.children).indexOf(bullet);
        const pos = positions();
        if (pos[i] !== undefined) goTo(pos[i]);
      };
      pag.addEventListener("click", onPagClick);
      cleanups.push(() => pag.removeEventListener("click", onPagClick));
    }

    update();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  const snapTrack =
    snap === "none" ? "" : snap === "mandatory" ? "snap-x snap-mandatory" : "snap-x snap-proximity";
  const snapItem = snap === "none" ? "" : "snap-start";
  const slideCount = React.Children.count(children);

  return (
    <div className={`swiper swiper-horizontal ${className}`}>
      <div
        ref={trackRef}
        className={`swiper-wrapper flex items-stretch overflow-x-auto overflow-y-hidden ${snapTrack} ${
          fullBleed ? "full-bleed-track" : ""
        } [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${trackClassName}`}
        style={{ WebkitOverflowScrolling: "touch", cursor: "grab" }}
      >
        {wrapItems
          ? React.Children.map(children, (child, idx) => (
              <div key={idx} className={`swiper-slide shrink-0 ${snapItem} ${itemClassName}`}>
                {child}
              </div>
            ))
          : children}
      </div>
      {scrollbar && (
        <div ref={barRef} className="swiper-scrollbar swiper-scrollbar-horizontal">
          <div ref={dragRef} className="swiper-scrollbar-drag" />
        </div>
      )}
      {navigation && (
        <>
          <div
            ref={prevRef}
            className="swiper-button-prev swiper-button-disabled"
            role="button"
            tabIndex={0}
            aria-label="Previous slide"
          />
          <div ref={nextRef} className="swiper-button-next" role="button" tabIndex={0} aria-label="Next slide" />
        </>
      )}
      {pagination && (
        <div ref={pagRef} className="swiper-pagination swiper-pagination-bullets swiper-pagination-horizontal">
          {Array.from({ length: slideCount }, (_, i) => (
            <span
              key={i}
              className={`swiper-pagination-bullet${i === 0 ? " swiper-pagination-bullet-active" : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
